import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatDateBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const LOGO_URL =
    "https://customer-assets-eiarnc6j.emergentagent.net/job_carpool-manager-6/artifacts/qyxqg7dd_logo%20Padr%C3%A3o.png";

const TIPO_LABELS = {
    onibus: "Ônibus", caminhonete: "Caminhonete", carro: "Carro",
    van: "Van", caminhao: "Caminhão", outro: "Outro",
};
const EMPR_LABELS = {
    km117: "Empreendimento KM 117", rio_cupari: "Rio Cupari",
    sede: "Sede COOMFLONA", setor_social: "Setor Social",
    nao_madeireiro: "Não Madeireiro", outro: "Outro",
};
const STATUS_LABELS = {
    autorizada: "AUTORIZADA", em_execucao: "EM EXECUÇÃO",
    concluida: "CONCLUÍDA", cancelada: "CANCELADA",
};

export default function ServiceOrderPrint() {
    const { id } = useParams();
    const { user } = useAuth();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        api.get(`/service-orders/${id}`)
            .then((res) => setOrder(res.data))
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [id]);

    const doPrint = () => window.print();
    const today = new Date().toLocaleDateString("pt-BR");

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
    }
    if (error || !order) {
        return (
            <div className="min-h-screen flex items-center justify-center flex-col gap-4">
                <div className="text-muted-foreground">OS não encontrada</div>
                <Button onClick={() => window.close()}>Fechar</Button>
            </div>
        );
    }

    return (
        <div className="print-root min-h-screen bg-gray-100 p-8" data-testid="os-print-page">
            <div className="no-print max-w-3xl mx-auto mb-4 flex items-center justify-between">
                <Button variant="outline" onClick={() => window.close()}>Fechar</Button>
                <Button onClick={doPrint} className="gap-2"><Printer className="w-4 h-4" /> Imprimir</Button>
            </div>

            <div className="ficha max-w-3xl mx-auto bg-white shadow-2xl relative overflow-hidden">
                <div className="h-2 bg-gradient-to-r from-blue-800 to-emerald-500" />

                {/* Header */}
                <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-300 bg-gradient-to-r from-blue-50 to-white">
                    <div className="w-16 h-16 bg-emerald-900 rounded-lg flex items-center justify-center p-1 shrink-0 shadow-md border-2 border-emerald-800">
                        <img src={LOGO_URL} alt="COOMFLONA" className="w-full h-full object-contain" />
                    </div>
                    <div className="flex-1">
                        <div className="font-extrabold text-base leading-tight text-blue-900">COOPERATIVA MISTA DA FLONA DO TAPAJÓS</div>
                        <div className="text-xs font-bold text-blue-800 tracking-wide">COOMFLONA — Setor de Transporte</div>
                        <div className="text-[10px] mt-0.5 text-gray-700">CNPJ 07.315.165/0001-00</div>
                    </div>
                    <div className="text-right">
                        <div className="inline-block px-2 py-1 rounded text-[10px] font-bold text-white bg-blue-800">{STATUS_LABELS[order.status]}</div>
                        <div className="text-[9px] text-gray-600 mt-1.5">Nº DA OS</div>
                        <div className="text-lg font-extrabold font-mono text-blue-900">#{String(order.numero || 0).padStart(4, "0")}</div>
                    </div>
                </div>

                <div className="bg-blue-900 text-white text-center py-2">
                    <div className="font-extrabold text-base">ORDEM DE SERVIÇO DE TRANSPORTE</div>
                </div>

                <div className="p-5 space-y-2.5 text-sm">
                    <div>
                        <SectionTitle>Autorização</SectionTitle>
                        <div className="grid grid-cols-2 gap-3 mt-1.5">
                            <Field label="Autorizada por" value={<strong className="text-blue-900">{order.autorizada_por || "—"}</strong>} />
                            <Field label="Data do serviço" value={formatDateBR(order.data_servico)} />
                        </div>
                    </div>

                    <div>
                        <SectionTitle>Solicitante / Empreendimento</SectionTitle>
                        <div className="grid grid-cols-2 gap-3 mt-1.5">
                            <Field label="Solicitante" value={order.solicitante || "—"} />
                            <Field label="Empreendimento" value={EMPR_LABELS[order.empreendimento] || "—"} />
                        </div>
                    </div>

                    <div>
                        <SectionTitle>Veículo e motorista</SectionTitle>
                        <div className="grid grid-cols-3 gap-3 mt-1.5">
                            <Field label="Tipo" value={TIPO_LABELS[order.tipo_veiculo]} />
                            <Field label="Placa" value={<span className="font-mono font-bold">{order.veiculo_placa || "—"}</span>} />
                            <Field label="Modelo" value={order.veiculo_modelo || "—"} />
                        </div>
                        <div className="mt-1.5">
                            <Field label="Motorista" value={order.motorista_nome || "—"} />
                        </div>
                    </div>

                    <div>
                        <SectionTitle>Serviço</SectionTitle>
                        <div className="mt-1.5">
                            <Field label="Destino" value={order.destino} />
                        </div>
                        <div className="mt-1.5">
                            <div className="text-[10px] uppercase tracking-wider text-gray-700 font-bold">Finalidade</div>
                            <div className="mt-0.5 font-semibold text-gray-900 border border-gray-500 rounded p-2 min-h-[40px] whitespace-pre-wrap">{order.finalidade}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mt-1.5">
                            <Field label="Hora saída" value={order.hora_saida || "—"} />
                            <Field label="Hora retorno" value={order.hora_retorno || "—"} />
                        </div>
                    </div>

                    {order.observacoes && (
                        <div>
                            <SectionTitle>Observações</SectionTitle>
                            <div className="mt-1 text-sm whitespace-pre-wrap bg-yellow-50 border-l-4 border-yellow-500 rounded p-2 text-gray-900">{order.observacoes}</div>
                        </div>
                    )}
                </div>

                <div className="px-5 pb-3 pt-8 grid grid-cols-3 gap-3">
                    <div className="text-center">
                        <div className="border-t-2 border-black pt-0.5 text-[10px] font-bold text-gray-900">Autorizada por</div>
                        <div className="text-[9px] text-gray-700 mt-0.5">{order.autorizada_por || "Nita"}</div>
                    </div>
                    <div className="text-center">
                        <div className="border-t-2 border-black pt-0.5 text-[10px] font-bold text-gray-900">Motorista</div>
                        <div className="text-[9px] text-gray-700 mt-0.5">{order.motorista_nome || "—"}</div>
                    </div>
                    <div className="text-center">
                        <div className="border-t-2 border-black pt-0.5 text-[10px] font-bold text-gray-900">Setor de Transporte</div>
                        <div className="text-[9px] text-gray-700 mt-0.5">{user?.name || "—"}</div>
                    </div>
                </div>

                <div className="px-5 py-1.5 border-t border-gray-300 bg-gray-100 flex justify-between items-center text-[9px] text-gray-700">
                    <div>Emitido em: <strong>{today}</strong></div>
                    <div>Sistema desenvolvido por <strong>Railan Farias</strong> (Cooperado)</div>
                </div>

                <div className="h-1 bg-gradient-to-r from-emerald-500 to-blue-800" />
            </div>

            <style>{`
                @media print {
                    body { background: white !important; }
                    .no-print { display: none !important; }
                    .print-root { padding: 0 !important; background: white !important; }
                    .ficha { box-shadow: none !important; max-width: 100% !important; width: 100% !important; page-break-inside: avoid; }
                    .ficha * { color: #000 !important; }
                    .ficha [class*="text-white"], .ficha [class*="bg-blue-800"] * , .ficha [class*="bg-blue-900"] * { color: #fff !important; }
                    @page { size: A4 portrait; margin: 8mm; }
                }
            `}</style>
        </div>
    );
}

function Field({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-700 font-bold">{label}</div>
            <div className="mt-0.5 font-semibold text-gray-900 border-b border-gray-500 pb-0.5 min-h-[20px]">{value || "—"}</div>
        </div>
    );
}

function SectionTitle({ children }) {
    return (
        <div className="text-[11px] font-extrabold uppercase tracking-widest text-blue-900 border-b-2 border-blue-700 pb-0.5">{children}</div>
    );
}
